import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  AuthState,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  LoginCredentials,
  ResetPasswordPayload,
} from "./auth.types";
import type { PermissionAction } from "./models/permission.model";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import {
  hasPermission as hasPermissionKey,
  hasAnyPermission,
  hasAllPermissions,
  hasResourcePermission,
  getUserPermissionKeys,
} from "../rbac/access";
import {
  changePassword as changePasswordService,
  forgotPassword as forgotPasswordService,
  resetPassword as resetPasswordService,
} from "./auth.service";
import type { IamSessionClient } from "./iam-session.types";
import { redirectAfterLogout } from "./suite-redirect";

export interface IamAuthProviderProps {
  children: React.ReactNode;
  api: IamSessionClient;
  /** Identifiant module Suite (edu, billing, …). Absent sur l'app Suite elle-même. */
  moduleId?: string;
  /** URL de base Suite (ex. http://localhost:3000). Requis si redirectOnLogout. */
  suiteUrl?: string;
  /** Rediriger vers Suite après déconnexion (défaut: true si moduleId + suiteUrl). */
  redirectOnLogout?: boolean;
  /** URL interne après déconnexion sur l'app Suite (ex. /login). */
  localLogoutPath?: string;
}

function sessionToState(
  session: NonNullable<Awaited<ReturnType<IamSessionClient["verifySession"]>>>,
): AuthState {
  return {
    user: session.user,
    tenant: session.tenant,
    membership: session.membership,
    tokens: session.tokens,
    isAuthenticated: true,
    isLoading: false,
  };
}

export function IamAuthProvider({
  children,
  api,
  moduleId,
  suiteUrl,
  redirectOnLogout,
  localLogoutPath,
}: IamAuthProviderProps) {
  const shouldRedirectOnLogout =
    redirectOnLogout ?? Boolean(moduleId && suiteUrl);

  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    membership: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const applySession = useCallback(
    (session: Awaited<ReturnType<IamSessionClient["verifySession"]>>) => {
      if (session) {
        setState(sessionToState(session));
      } else {
        setState({
          user: null,
          tenant: null,
          membership: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    },
    [],
  );

  const runVerifySession = useCallback(async () => {
    try {
      const session = await api.verifySession();
      applySession(session);
      return session;
    } catch {
      applySession(null);
      return null;
    }
  }, [api, applySession]);

  const verifySession = useCallback(async () => {
    await runVerifySession();
  }, [runVerifySession]);

  useEffect(() => {
    let mounted = true;
    const safetyTimer = window.setTimeout(() => {
      if (mounted) {
        setState((prev) =>
          prev.isLoading ? { ...prev, isLoading: false } : prev,
        );
      }
    }, 12_000);

    void api
      .verifySession()
      .then((session) => {
        if (mounted) applySession(session);
      })
      .catch(() => {
        if (mounted) applySession(null);
      })
      .finally(() => {
        window.clearTimeout(safetyTimer);
      });

    return () => {
      mounted = false;
      window.clearTimeout(safetyTimer);
    };
  }, [api, applySession]);

  useEffect(() => {
    const recheck = () => {
      void runVerifySession();
    };

    window.addEventListener("focus", recheck);
    const onVisibility = () => {
      if (document.visibilityState === "visible") recheck();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [runVerifySession]);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const session = await api.login(credentials);
        setState(sessionToState(session));
      } catch (error) {
        setState((prev) => ({ ...prev, isLoading: false }));
        throw error;
      }
    },
    [api],
  );

  const logout = useCallback(async () => {
    const centralLogoutPath =
      localLogoutPath === "/auth/logout" ||
      localLogoutPath?.endsWith("/auth/logout");

    if (centralLogoutPath && localLogoutPath) {
      applySession(null);
      const path = localLogoutPath.startsWith("/")
        ? localLogoutPath
        : `/${localLogoutPath}`;
      window.location.replace(path);
      return;
    }

    // Module satellite (Edu, Inventory…) — rediriger tout de suite vers Suite /auth/logout
    // pour éviter une course avec ProtectedRoute → relay SSO (session Suite encore active).
    if (shouldRedirectOnLogout && suiteUrl) {
      redirectAfterLogout(suiteUrl);
      return;
    }

    await api.logout();
    applySession(null);

    if (localLogoutPath) {
      const path = localLogoutPath.startsWith("/") ? localLogoutPath : `/${localLogoutPath}`;
      window.location.replace(path);
    }
  }, [api, applySession, shouldRedirectOnLogout, suiteUrl, localLogoutPath]);

  const refreshToken = useCallback(async () => {
    if (!state.tokens?.refreshToken) return;
    const tokens = await api.refreshToken(state.tokens.refreshToken);
    setState((prev) => ({ ...prev, tokens }));
  }, [api, state.tokens?.refreshToken]);

  const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
    await changePasswordService(payload);
  }, []);

  const forgotPassword = useCallback(async (payload: ForgotPasswordPayload) => {
    await forgotPasswordService(payload);
  }, []);

  const resetPassword = useCallback(async (payload: ResetPasswordPayload) => {
    await resetPasswordService(payload);
  }, []);

  const hasPermission = useCallback(
    (permissionKey: string) => hasPermissionKey(state.user, permissionKey),
    [state.user],
  );

  const hasAnyPermissionCb = useCallback(
    (permissionKeys: string[]) => hasAnyPermission(state.user, permissionKeys),
    [state.user],
  );

  const hasAllPermissionsCb = useCallback(
    (permissionKeys: string[]) => hasAllPermissions(state.user, permissionKeys),
    [state.user],
  );

  const hasResourcePermissionCb = useCallback(
    (resource: string, action?: PermissionAction) =>
      hasResourcePermission(state.user, resource, action),
    [state.user],
  );

  const permissionKeys = useMemo(
    () => getUserPermissionKeys(state.user),
    [state.user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      refreshToken,
      changePassword,
      forgotPassword,
      resetPassword,
      verifySession,
      hasPermission,
      hasAnyPermission: hasAnyPermissionCb,
      hasAllPermissions: hasAllPermissionsCb,
      hasResourcePermission: hasResourcePermissionCb,
      permissionKeys,
    }),
    [
      state,
      login,
      logout,
      refreshToken,
      changePassword,
      forgotPassword,
      resetPassword,
      verifySession,
      hasPermission,
      hasAnyPermissionCb,
      hasAllPermissionsCb,
      hasResourcePermissionCb,
      permissionKeys,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
